import { FunctionComponent, useState } from 'react';
import cn from 'classnames';
import { useForm, FormProvider, SubmitHandler } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Button,
  Section,
  SectionHeading,
  TextField,
  NumberField,
  DateField,
  SelectField,
  CheckBoxField,
  Address,
} from '../../components';
import { GaIFormValues } from '../../types';
import { cleanIntakeDates } from '../../utils/intake';
import { successCallbackPath } from '../../utils/paths';
import { fetchApi } from '../../utils/fetchApi';
import { ApiStatus, IApiData } from '../../utils/types';
import { ManagedReqsByState } from './GenericIntake';
import { useScrollIntoView } from '../../hooks';
import { FormError } from '../../components/forms/FormError';

type GaIntakeProps = {
  onSubmitSuccess?: () => void;
  onEnterLater?: () => void;
  isFullWidth?: boolean;
  scrollOnMount?: boolean;
};

export const GaIntake: FunctionComponent<GaIntakeProps> = ({
  onSubmitSuccess,
  onEnterLater,
  isFullWidth,
  scrollOnMount,
}) => {
  const [scrollIntoViewRef] = useScrollIntoView(scrollOnMount);
  const [searchParams] = useSearchParams();
  const callbackUrl = searchParams.get('callback_url');

  const [hasSubmitError, setHasSubmitError] = useState<boolean>(false);

  const navigate = useNavigate();

  const formMethods = useForm<GaIFormValues>();
  const {
    handleSubmit,
    formState: { errors },
  } = formMethods;

  const handleSubmitSuccess = () =>
    onSubmitSuccess ? onSubmitSuccess() : navigate(`/state-setup/ga/success`);

  const onSubmit: SubmitHandler<GaIFormValues> = (data) => {
    setHasSubmitError(false);
    const cleanedData = cleanIntakeDates(data);
    const payload = { data: cleanedData };

    fetchApi({
      url: `/api/form_captures/ga`,
      method: 'POST',
      body: payload,
    }).then(({ status }: IApiData) => {
      if (status === ApiStatus.Error) {
        setHasSubmitError(true);
      } else if (ManagedReqsByState.ga) {
        const reqIds: string[] = ManagedReqsByState.ga;
        // Set the requirement to managed and in-progress
        fetchApi({
          url: `/api/requirements/batch`,
          method: 'PATCH',
          body: {
            patch: { operation: 'mark-as-managed' },
            data_ids: reqIds,
          },
        }).then(({ status: status2 }: IApiData) => {
          if (status2 === ApiStatus.Error) {
            setHasSubmitError(true);
          } else {
            // Create and start workflow for requirements
            fetchApi({
              url: `/api/workflows`,
              method: 'POST',
              body: {
                data_ids: reqIds,
                region_code: 'ga',
              },
            }).then(({ status: status3 }: IApiData) => {
              if (status3 === ApiStatus.Error) {
                setHasSubmitError(true);
              } else if (callbackUrl) {
                navigate(successCallbackPath(callbackUrl), { replace: true });
              } else {
                handleSubmitSuccess();
              }
            });
          }
        });
      } else {
        handleSubmitSuccess();
      }
    });
  };

  const thisYear = new Date().getFullYear();
  const yearOptions = [];
  for (let y = thisYear - 10; y <= thisYear; y += 1) {
    yearOptions.push({ value: y });
  }

  return (
    <Section className="flex flex-col items-center" ref={scrollIntoViewRef}>
      <SectionHeading
        className={cn('pb-4', {
          'w-full': isFullWidth,
          'w-96': !isFullWidth,
        })}
        text="Georgia Employer Setup"
      />
      <FormProvider {...formMethods}>
        <form
          className={cn('my-8 ', {
            'w-full': isFullWidth,
            'w-96': !isFullWidth,
          })}
          onSubmit={handleSubmit(onSubmit)}
        >
          <TextField
            name="dba_name"
            label="Alternative Business Name (optional)"
            placeholder="Acme's Trade Name"
            description="Trade name or DBA if different from the company's legal business name."
            error={errors.dba_name}
          />
          <DateField
            name="activity_start_date"
            label="New Hire Start Date"
            description="The Georgia Department of Labor requires that employees must have started prior to opening an unemployment insurance account. If the start date is in the future, registration won't occur until after the start date."
            error={errors.activity_start_date}
            reactFormConfig={{
              required: 'This is required',
            }}
          />
          <DateField
            name="first_payroll_date"
            label="Date of First Payroll"
            error={errors.first_payroll_date}
            reactFormConfig={{
              required: 'This is required',
            }}
          />
          <NumberField
            name="employees.num_employed"
            label="Number of Employees in Georgia"
            min="1"
            error={errors.employees?.num_employed}
            reactFormConfig={{
              required: 'This is required',
            }}
          />
          <CheckBoxField
            name="employees.all_over_18"
            label="I confirm that all employees are 18 and older"
            labelClassName="font-normal text-sm text-gray-800 mt-0.5"
            className="mb-8"
            error={errors.employees?.all_over_18}
            reactFormConfig={{
              required: 'This is required',
            }}
          />
          <NumberField
            name="num_ga_locations"
            label="Number of Georgia Locations"
            description="If you only have employees working from home enter 0."
            min="0"
            error={errors.num_ga_locations}
            reactFormConfig={{
              required: 'This is required',
            }}
          />
          <SelectField
            name="fut_filing_year"
            label="First Federal Unemployment Tax Year"
            description="Please provide the first year you filed and paid or will file and pay FUTA tax."
            options={yearOptions.reverse()} // descending order
            error={errors.fut_filing_year}
          />
          <SelectField
            name="business_category"
            label="Business Category"
            description="Please select the category that best describes the nature of your business."
            options={[
              { value: 'Accomodation and Food Services' },
              { value: 'Arts, Entertainment, Recreation and Sports' },
              { value: 'Construction' },
              { value: 'Educational Services and Day Care' },
              { value: 'Finance and Insurance' },
              { value: 'Fishing and Hunting/Trapping' },
              { value: 'Forestry and Logging' },
              { value: 'Health Care and Social Assistance' },
              { value: 'Information and Telecommunications' },
              { value: 'Mail Services' },
              { value: 'Management of Companies and Enterprises' },
              { value: 'Manufacturing' },
              { value: 'Mining and Utilities' },
              { value: 'Printing and Publishing Services' },
              { value: 'Professional and Technical Services' },
              { value: 'Rental and Leasing, Including Real Estate' },
              { value: 'Retail Sales' },
              { value: 'Services - Personal and Other' },
              { value: 'Transportation' },
              { value: 'Warehousing' },
              { value: 'Wholesale Trade Merchants' },
              { value: 'None of these match my business' },
            ]}
            error={errors.business_category}
          />
          <NumberField
            name="estimated_quarterly_wages"
            label="Estimated Quarterly Wages"
            description="Please provide an estimate of Georgia wages in a full quarter."
            isCurrency
            min="0"
            error={errors.estimated_quarterly_wages}
            reactFormConfig={{
              required: 'This is required',
            }}
          />
          <div className="mb-8">
            <div>
              <label className="mb-2 block font-semibold">
                Georgia Address
              </label>
              <p className="mb-4 text-sm text-gray-600">
                Please provide the address of your principal location in
                Georgia. Georgia accepts an employee&apos;s address if all
                employees are remote. The address cannot be a P.O. Box.
              </p>
              <CheckBoxField
                name="physical_address.is_employee_address"
                label="This is an employee's address"
                labelClassName="font-normal text-sm text-gray-800 mt-0.5"
                className="mb-1 rounded-t"
                error={errors.physical_address?.is_employee_address}
              />
              <Address
                error={errors.physical_address}
                name="physical_address"
                defaultState="Georgia"
              />
            </div>
          </div>
          <div className="mt-4 flex">
            <div className="flex gap-x-4">
              {onEnterLater && (
                <Button
                  type="button"
                  variant="grayTertiary"
                  size="large"
                  isFullWidth
                  onClick={onEnterLater}
                >
                  Enter later
                </Button>
              )}
              <Button type="submit" isFullWidth size="large">
                Submit
              </Button>
            </div>
            {hasSubmitError && <FormError />}
          </div>
        </form>
      </FormProvider>
    </Section>
  );
};

export default GaIntake;
